/**
 * @license
 * Copyright (c) 2023 Reiji Terasaka
 * SPDX-License-Identifier: MIT
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import Blockly from "blockly";
import { javascriptGenerator } from 'blockly/javascript';
import * as JA from 'blockly/msg/ja';
import { Interpreter } from "./interpreter/interpreter";
import { sleep } from "./util";
const options = {
    types: [
        {
            description: "JSON",
            accept: {
                "application/json": [".json"],
            },
        },
    ],
};
export class BlocklyToolkit {
    constructor(view, toolboxJson, theme, customBlock) {
        this.listeners = [];
        this.sleepTime = 500;
        this.isRunnning = false;
        Blockly.setLocale(JA);
        this.customBlock = customBlock || [];
        for (const b of this.customBlock) {
            Blockly.Blocks[b.name] = {
                init: b.init,
            };
            javascriptGenerator.forBlock[b.name] = b.codeGenerator;
        }
        this.workspace = Blockly.inject(view, {
            toolbox: toolboxJson,
            theme: theme,
            trashcan: true,
            scrollbars: true,
        });
        javascriptGenerator.addReservedWords('highlightBlock');
        javascriptGenerator.addReservedWords('sleepBlock');
    }
    highlightBlock(id) {
        this.workspace.highlightBlock(id);
    }
    ;
    addEventListener(type, listener) {
        this.listeners.push({ type: type, listener: listener });
    }
    dispatchEvent(type, ev) {
        for (const listener of this.listeners) {
            if (listener.type === type) {
                try {
                    listener.listener.call(this, ev);
                }
                catch (error) {
                    console.error(error);
                }
            }
        }
    }
    setSleepTime(sleepTime) {
        this.sleepTime = sleepTime;
    }
    getCode(statementPrefix) {
        javascriptGenerator.STATEMENT_PREFIX = statementPrefix;
        return javascriptGenerator.workspaceToCode(this.workspace);
    }
    run() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isRunnning)
                return;
            var code = this.getCode("highlightBlock(%1);\n");
            const context = { sleepTime: 0 };
            const myInterpreter = new Interpreter(code, (interpreter, globalObject) => {
                const wrapperAlert = (msg) => {
                    this.dispatchEvent("console", msg);
                };
                interpreter.setProperty(globalObject, 'alert', interpreter.createNativeFunction(wrapperAlert));
                const wrapperPrompt = (msg) => {
                    return window.prompt(msg);
                };
                interpreter.setProperty(globalObject, 'prompt', interpreter.createNativeFunction(wrapperPrompt));
                const wrapperHighlight = (id) => {
                    this.highlightBlock(id);
                    context.sleepTime = this.sleepTime;
                };
                interpreter.setProperty(globalObject, 'highlightBlock', interpreter.createNativeFunction(wrapperHighlight));
                for (const b of this.customBlock) {
                    b.interpreterInitFunc(interpreter, globalObject, context);
                }
            });
            this.highlightBlock(null);
            this.isRunnning = true;
            this.dispatchEvent("changestate", "runnning");
            this.dispatchEvent("console", "実行開始");
            try {
                while (true) {
                    const next = myInterpreter.step();
                    if (!next) {
                        break;
                    }
                    if (context.sleepTime > 0) {
                        yield sleep(context.sleepTime);
                        context.sleepTime = 0;
                        if (!this.isRunnning) {
                            this.dispatchEvent("console", "強制終了");
                            break;
                        }
                    }
                }
                yield sleep(this.sleepTime);
            }
            catch (e) {
                alert(e);
            }
            this.dispatchEvent("console", "実行終了");
            this.highlightBlock(null);
            this.isRunnning = false;
            this.dispatchEvent("changestate", "stopped");
        });
    }
    stop() {
        this.isRunnning = false;
    }
    save() {
        return __awaiter(this, void 0, void 0, function* () {
            const text = JSON.stringify(Blockly.serialization.workspaces.save(this.workspace));
            const handle = yield window.showSaveFilePicker(options);
            const writable = yield handle.createWritable();
            yield writable.write(text);
            yield writable.close();
        });
    }
    ;
    load() {
        return __awaiter(this, void 0, void 0, function* () {
            const handle = yield window.showOpenFilePicker(options);
            const file = yield handle[0].getFile();
            const text = JSON.parse(yield file.text());
            Blockly.serialization.workspaces.load(text, this.workspace);
        });
    }
    ;
}
